import React, { useEffect, useState, useCallback } from "react";
import {
	Box,
	Typography,
	CircularProgress,
	Alert,
	Link,
	Divider,
	IconButton,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useGrade } from "../../../contexts/GradeContext";
import { CARD_SURFACE } from "../../../components/teacher/module/teacherModuleStyles";
import GameData from "../GameData";

/**
 * Full lesson player at /teacher/videos/:gradeId/:categoryId — same experience as
 * /game-data/:categoryId, with teacher shell chrome and topic/grade controls in-card.
 */
const TeacherVideos = () => {
	const { gradeId, categoryId } = useParams();
	const { user, supabase } = useAuth();
	const { updateSelectedGrade } = useGrade();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [gradeName, setGradeName] = useState("");
	const [categoryName, setCategoryName] = useState("");

	const load = useCallback(async () => {
		if (!user?.id || !gradeId || !categoryId) return;
		setLoading(true);
		setError(null);
		try {
			const { data: allowed, error: aErr } = await supabase
				.from("teacher_grades")
				.select("id")
				.eq("profile_id", user.id)
				.eq("grade_id", gradeId)
				.maybeSingle();

			if (aErr) throw aErr;
			if (!allowed) {
				setError("You don't have access to this grade.");
				return;
			}

			updateSelectedGrade(gradeId);

			const [{ data: gradeRow, error: gErr }, { data: catRow, error: cErr }] =
				await Promise.all([
					supabase.from("grades").select("name").eq("id", gradeId).single(),
					supabase
						.from("course_categories")
						.select("name")
						.eq("id", categoryId)
						.single(),
				]);

			if (gErr) throw gErr;
			if (cErr) throw cErr;
			setGradeName(gradeRow?.name || "");
			setCategoryName(catRow?.name || "Topic");
		} catch (e) {
			console.error(e);
			setError(e.message || "Failed to load topic");
		} finally {
			setLoading(false);
		}
	}, [user?.id, gradeId, categoryId, supabase, updateSelectedGrade]);

	useEffect(() => {
		load();
	}, [load]);

	const goTopics = () => navigate(`/teacher/topics/${gradeId}`);

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
				<CircularProgress size={36} sx={{ color: "#16a34a" }} />
			</Box>
		);
	}

	return (
		<Box>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{!error && (
				<Box sx={{ ...CARD_SURFACE, p: 0, overflow: "hidden" }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: { xs: "column", sm: "row" },
							alignItems: { xs: "stretch", sm: "center" },
							justifyContent: "space-between",
							gap: { xs: 1.5, sm: 2 },
							px: { xs: 2, sm: 2.5 },
							pt: { xs: 2, sm: 2.5 },
							pb: 2,
							borderBottom: "1px solid",
							borderColor: "divider",
						}}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								minWidth: 0,
							}}>
							<IconButton
								aria-label="Back to topics"
								onClick={goTopics}
								size="small"
								sx={{
									color: "#1e3a5f",
									flexShrink: 0,
									"&:hover": { backgroundColor: "rgba(30, 58, 95, 0.08)" },
								}}>
								<ChevronLeft size={22} strokeWidth={2.5} />
							</IconButton>
							<Typography
								sx={{
									fontWeight: 800,
									color: "#1e3a5f",
									fontSize: { xs: "1.25rem", sm: "1.35rem" },
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{categoryName}
							</Typography>
						</Box>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: { xs: "flex-start", sm: "flex-end" },
								gap: 1,
								flexWrap: "wrap",
							}}>
							<Typography
								variant="body2"
								sx={{ color: "text.secondary", fontWeight: 600 }}>
								{gradeName || "Grade"}
							</Typography>
							<Divider
								orientation="vertical"
								flexItem
								sx={{ borderColor: "divider", alignSelf: "stretch", my: 0.25 }}
							/>
							<Link
								component="button"
								type="button"
								variant="body2"
								onClick={goTopics}
								underline="hover"
								sx={{
									cursor: "pointer",
									fontWeight: 600,
									display: "inline-flex",
									alignItems: "center",
									gap: 0.25,
								}}>
								Change topic
								<ChevronRight size={16} />
							</Link>
						</Box>
					</Box>

					<GameData
						gradeId={gradeId}
						categoryId={categoryId}
						hideTopHeader
						hideLessonDescription
						disableContentQuizzes
						autoMarkCompleteOnVideoEnd
					/>
				</Box>
			)}

			<Box
				sx={{
					mt: 3,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 1.25,
					py: 1,
				}}>
				<img
					src="/logo.png"
					alt="Funmiyo"
					style={{ height: 40, width: "auto", objectFit: "contain" }}
				/>
			</Box>
		</Box>
	);
};

export default TeacherVideos;
